import React, { useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tooltip } from 'primereact/tooltip';


const ContactTable = ({
  contactData,
  setInteraction,
  setStructurePDBIdIntra,
  setStructurePDBIdInter,
  interactionReset,
  type,
  selectedRows,
  setSelectedRows,
  setSelectedUniProtAccession,
}) => {
  const tableRef = useRef(null);
  const mapData = require('../../map_gene_pro.json');

  /** Column definitions */
  const contactColumns = [
    { field: 'anPosition', header: 'Position' },
    { field: 'proteinUid', header: 'Uniprot Accession' },
    { field: 'geneSymbol', header: 'Gene' },
    { field: 'identifier', header: 'Identifier' },
    { field: 'contactPosition', header: 'Contact Position' },
    { field: 'distance', header: 'Distance (Å)' },
    { field: 'details', header: 'Atoms' }
  ];

  // Create a map for quick lookup
  const proteinToGeneMap = Object.fromEntries(
    mapData.map((item) => [item.PROTEIN_AN, item.GENE_SYMBOL])
  );

  // Add geneSymbol to each row based on proteinUid
  const mappedContactData = contactData?.map((row) => ({
    ...row,
    geneSymbol: proteinToGeneMap[row.proteinUid] || 'N/A',
    rowKey: `${row.anPosition}|${row.identifier}|${row.contactPosition}|${row.details}`,
  }));

  /** Numeric filter widget */
  const renderNumericFilter = (options) => (
    <InputText
      type="number"
      value={options.value ?? ''}
      onChange={(e) => options.filterCallback(e.target.value, options.index)}
      placeholder="Enter number"
      className="p-column-filter"
    />
  );

  /** Build <Column/> list */
  const renderContactColumns = contactColumns.map((col) => {
    const isNumeric = col.field === 'distance' || col.field === 'contactPosition';
    return (
      <Column
        key={col.field}
        field={col.field}
        header={col.header}
        sortable
        filter
        filterDisplay="menu"
        dataType={isNumeric ? 'numeric' : 'text'}
        filterElement={isNumeric ? renderNumericFilter : undefined}
        filterPlaceholder={`Search by ${col.header}`}
      />
    );
  });

  /**
   * Main table selection handler.
   * Prevents deselection when clicking an already-selected row.
   */
  const onSelectionChangeHandler = (e) => {
    const newSelection = e.value;

    if (selectedRows && !newSelection) {
      return;
    }

    setSelectedRows(newSelection);

    if (newSelection) {
        const parsedAnPos =
        typeof newSelection.anPosition === 'string' && newSelection.anPosition.includes('_')
            ? parseInt(newSelection.anPosition.split('_')[1], 10)
            : newSelection.anPosition;
        const parsedDistance = newSelection.distance !== undefined ? parseFloat(newSelection.distance) : 0;
        setInteraction({ x: parsedAnPos, y: newSelection.contactPosition, distance: parsedDistance, rowType: 'main', details: newSelection.details });

        if (type === 'intra') setStructurePDBIdIntra(newSelection.identifier)
        else if (type === 'inter') setStructurePDBIdInter(newSelection.identifier);
        if (type === 'inter') setSelectedUniProtAccession(newSelection.proteinUid);

    }
  };

  /** Conditionally prevent row selection if `details` is empty */
  const isRowSelectable = (e) => {
    return !!e.data.details;
  };

  /** Applies a custom CSS class to change the cursor for non-selectable rows. */
  const rowClass = (data) => {
    return {
      'not-clickable': !data.details
    };
  };

  const resetAll = () => {
    setSelectedRows(null);
    interactionReset();
  };

  /** CSV / XLSX / PDF exports */
  const exportCSV = () => tableRef.current.exportCSV();

  const exportExcel = async () => {
    const xlsx = await import('xlsx');
    const worksheet = xlsx.utils.json_to_sheet(contactData);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const buffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
    const { default: saveAs } = await import('file-saver');
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(blob, `contacts_${Date.now()}.xlsx`);
  };

  const exportPdf = async () => {
    const jsPDF = (await import('jspdf')).default;
    await import('jspdf-autotable');
    const doc = new jsPDF('landscape');
    const columns = contactColumns.map((c) => ({ title: c.header, dataKey: c.field }));
    doc.autoTable(columns, contactData);
    doc.save('contacts.pdf');
  };

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    proteinUid: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    geneSymbol: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    identifier: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    contactPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    details: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  });

  /** Global search */
  const value = filters['global'] ? filters['global'].value : '';

  const onGlobalFilterChange = (event) => {
    const value = event.target.value.replace(/\s+/g, "");
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
  };

  const clearFilter = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      geneSymbol: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      proteinUid: { operator: FilterOperator.AND, constraints: [{ value:null, matchMode: FilterMatchMode.CONTAINS }] },
      identifier: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      contactPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      details: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    });
  };

    const tagsBodyTemplate = (rowData) => {
    if (!rowData.summary || !rowData.anPosition) return null;
    const annotations = rowData.summary;
    if (!annotations) return null;

    return (
      <div className="flex gap-1 justify-content-center">
        {annotations.is_functional && (
         <>
           <Tooltip target=".is_functional" />
           <i className="is_functional pi pi-cog p-1" style={{ color: '#708090' }} data-pr-tooltip="Functional Significance"
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_clinvar && (
         <>
          <Tooltip target=".has_clinvar" />
          <i className="has_clinvar pi pi-shield p-1" style={{ color: '#708090' }} data-pr-tooltip="Has ClinVar variant"
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        { ( annotations.has_intra_contact && type !== 'intra') && (
         <>
          <Tooltip target=".has_intra_contact" />
          <i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {( annotations.has_intra_contact && type !== 'intra') && (
         <>
          <Tooltip target=".has_af_intra_contact" />
          <i className="has_af_intra_contact pi pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
          { ( annotations.has_inter_contact && type !== 'inter') && (
          <>
          <Tooltip target=".has_inter_contact" />
          <i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Contact"
           data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
          </>
        )}
        {( annotations.has_af_inter_contact  && type !== 'inter') && (
         <>
          <Tooltip target=".has_af_inter_contact" />
          <i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Inter-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
         { annotations.has_ligand  && (
          <>
          <Tooltip target=".has_ligand" />
          <i className="has_ligand pi pi-ethereum p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Ligand Interaction"
           data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
          </>
        )}
      </div>
    );
  };

  /** Header */
  const header = (
    <div className="row align-items-center">
      <div className="col mb-2 d-flex align-items-center">
        <IconField iconPosition="left" className="me-1">
          <InputIcon className="pi pi-search" />
          <InputText
            type="search"
            value={value || ''} onChange={onGlobalFilterChange}
            className="vw-10"
            placeholder="Global Search"
          />
        </IconField>
        <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} />
        <Button icon="pi pi-replay" text onClick={resetAll} tooltip="Clear selection" />
      </div>
      <div className="col d-flex justify-content-end align-items-center">
        <Button icon="pi pi-file" className="p-button-rounded p-mr-2" onClick={exportCSV} tooltip="CSV" />
        <Button
          icon="pi pi-file-excel"
          className="p-button-success p-button-rounded p-mr-2"
          onClick={exportExcel}
          tooltip="XLSX"
        />
        <Button
          icon="pi pi-file-pdf"
          className="p-button-warning p-button-rounded"
          onClick={exportPdf}
          tooltip="PDF"
        />
      </div>
    </div>
  );

  /** Main render */
  return (
    <DataTable
      ref={tableRef}
      value={mappedContactData}
      dataKey="rowKey"
      paginator
      rows={5}
      removableSort
      header={header}
      filters={filters}
      onFilter={(e) => setFilters(e.filters)}
      globalFilterFields={contactColumns.map((c) => c.field)}
      responsiveLayout="scroll"
      selectionMode="single"
      selection={selectedRows}
      onSelectionChange={onSelectionChangeHandler}
      isDataSelectable={isRowSelectable}
      rowClassName={rowClass}
      filterDisplay="menu"
      emptyMessage="No contacts found."
      className="nobreak"
      tableStyle={{ minWidth: '60rem' }}
    >
    <Column
                  header="Tags"
                  body={tagsBodyTemplate}
                  style={{ width: '5rem' }}
                  align="center"
              />
      {renderContactColumns}
    </DataTable>
  );
};

export default ContactTable;
